/* eslint-disable @nx/workspace/cds-react-no-missing-component-files */
import * as React from 'react'

import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'

import CheckboxCheckedIcon from '../../../icons/CheckboxCheckedIcon'
import CheckboxUncheckedIcon from '../../../icons/CheckboxUncheckedIcon'
import { Flex } from '../../layout/Flex'
import LoadingIcon from '../../LoadingIcon/LoadingIcon'

export type BaseCheckboxInputLabelProps = {
  checked?: boolean
  disabled?: boolean
  invalid?: boolean
  variant?: 'primary' | 'secondary'
  highlight?: boolean
  onFocus?: React.InputHTMLAttributes<HTMLInputElement>['onFocus']
  onBlur?: React.InputHTMLAttributes<HTMLInputElement>['onBlur']
}

export type BaseCheckboxInputProps = {
  checkedByDefault?: boolean
  className?: string
  value?: string
  label?: string | React.ReactNode
  loading?: boolean
  subtext?: string
  onClick?: React.InputHTMLAttributes<HTMLInputElement>['onClick']
  onChange?: React.InputHTMLAttributes<HTMLInputElement>['onChange']
  tooltip?: React.ReactNode
} & BaseCheckboxInputLabelProps &
  Pick<React.HTMLAttributes<HTMLElement>, 'aria-label'>

const CheckboxTextContainer = styled.div`
  width: 100%;
  ${props => props.theme.breakpoints.tablet} {
    width: auto;
  }
`

const CheckboxLabelContainer = styled.div`
  flex: 1;
  display: flex;
`

const TooltipContainer = styled.div`
  height: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-left: 0.625rem;
  margin-right: ${props => props.theme.spacing.xs};
`

const CheckboxLabel = styled.span`
  margin-left: ${props => props.theme.spacing.m};
  flex: 1;
  display: flex;
  align-items: center;
`
const SubText = styled(CheckboxLabel)`
  color: ${({ theme }) => theme.colors.secondaryLabel};
  ${({ theme }) => theme.typography.body}
  white-space: pre-wrap;
`

const HiddenInput = styled.input`
  opacity: 0;
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  cursor: pointer;
`

const IconFocusWrapper = styled.div`
  ${({ theme }) => `
    ${theme.selectors.keyboard} &:focus-within {
      outline: 0.5rem solid ${theme.addOpacity(theme.colors.primary, 0.2)};
      border-radius: 32%;
    }
  `};
  height: 1.5rem;
  position: relative;
`

type CheckboxProps = Pick<BaseCheckboxInputProps, 'checked' | 'loading' | 'disabled' | 'variant'> &
  Pick<BaseCheckboxInputProps, 'aria-label'>

const Checkbox = ({ checked, loading, disabled, variant, 'aria-label': ariaLabel }: CheckboxProps) => {
  const theme = useTheme()
  if (loading) {
    return <LoadingIcon color="checkboxSelected" size={24} />
  }

  const checkboxProps = {
    role: 'checkbox',
    'aria-checked': checked,
    'aria-disabled': disabled,
    'aria-label': ariaLabel,
    opacity: disabled ? 0.5 : 1,
  }

  return checked ? (
    <CheckboxCheckedIcon {...checkboxProps} data-testid="checkbox-checked-icon" />
  ) : (
    <CheckboxUncheckedIcon
      {...checkboxProps}
      data-testid="checkbox-unchecked-icon"
      color={variant === 'secondary' ? theme.colors.withinGrayCardInputOutline : theme.colors.inputOutline}
    />
  )
}

const BaseCheckboxInput = React.forwardRef<HTMLInputElement, BaseCheckboxInputProps>(
  (
    {
      checked,
      value,
      disabled,
      loading,
      label,
      subtext,
      variant,
      tooltip,
      'aria-label': ariaLabel,
      onChange,
      ...props
    },
    ref
  ) => {
    return (
      <Flex flexDirection="row">
        <IconFocusWrapper>
          <HiddenInput
            type="checkbox"
            {...props}
            value={value}
            checked={checked}
            disabled={disabled}
            ref={ref}
            data-testid="hidden-checkbox"
            onChange={e => {
              onChange?.(e)
              e.currentTarget.focus()
            }}
          />
          <Checkbox
            aria-label={ariaLabel}
            checked={checked}
            disabled={disabled}
            variant={variant}
            data-testid="visible-checkbox"
            loading={loading}
          />
        </IconFocusWrapper>
        {label || tooltip || subtext ? (
          <CheckboxTextContainer>
            <CheckboxLabelContainer>
              {label && <CheckboxLabel>{label}</CheckboxLabel>}
              {tooltip && <TooltipContainer>{tooltip}</TooltipContainer>}
            </CheckboxLabelContainer>
            {subtext && <SubText>{subtext}</SubText>}
          </CheckboxTextContainer>
        ) : null}
      </Flex>
    )
  }
)

export default BaseCheckboxInput
